import type { FeatureFlag, UserGroup } from "~/api/API";

export interface FeatureFlagEvaluationContext {
  userId?: string;
  userRole?: UserGroup;
  doctorId?: string;
}

/** The fields of a flag row that targeting reads. */
export type FeatureFlagTargeting = Pick<
  FeatureFlag,
  | "enabled"
  | "enabledForUserIds"
  | "enabledForRoles"
  | "enabledForDoctorIds"
  | "disabledForUserIds"
  | "disabledForRoles"
  | "disabledForDoctorIds"
>;

/**
 * Whether a flag row is on for the context. Kept free of side-effecting imports
 * so scripts (list-view parity) evaluate flags with the Portal's exact rule.
 * Mirrors `FeatureFlags.is_enabled` in `services/feature_flags/flags.py`.
 */
export function evaluateFeatureFlag(
  flag: FeatureFlagTargeting,
  context: FeatureFlagEvaluationContext,
): boolean {
  // Kill switch
  if (!flag.enabled) {
    return false;
  }

  // Exclusions win over every allowlist
  if (
    (context.userId && flag.disabledForUserIds?.includes(context.userId)) ||
    (context.userRole && flag.disabledForRoles?.includes(context.userRole)) ||
    (context.doctorId && flag.disabledForDoctorIds?.includes(context.doctorId))
  ) {
    return false;
  }

  // No allowlist: on for everyone
  if (
    !flag.enabledForUserIds?.length &&
    !flag.enabledForRoles?.length &&
    !flag.enabledForDoctorIds?.length
  ) {
    return true;
  }

  // Allowlists use OR logic
  if (context.userId && flag.enabledForUserIds?.includes(context.userId)) {
    return true;
  }
  if (context.userRole && flag.enabledForRoles?.includes(context.userRole)) {
    return true;
  }
  if (
    context.doctorId &&
    flag.enabledForDoctorIds?.includes(context.doctorId)
  ) {
    return true;
  }
  return false;
}
